import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import styles from './console.css';
import { runPython, workspaceToPython } from '../../lib/pixelblock';

const Console = () => {
    const intl = useIntl();
    const [isExpanded, setIsExpanded] = useState(false);
    const [output, setOutput] = useState('');
    const [status, setStatus] = useState('idle'); // 'idle' | 'running' | 'success' | 'error'
    const bodyRef = useRef(null);

    // Height state with localStorage persistence
    const [height, setHeight] = useState(() => {
        try {
            const saved = localStorage.getItem('pixelblock_console_height');
            return saved ? Math.max(70, Math.min(600, parseInt(saved, 10))) : 180;
        } catch (e) {
            return 180;
        }
    });

    // Font size state with localStorage persistence
    const [fontSize, setFontSize] = useState(() => {
        try {
            const saved = localStorage.getItem('pixelblock_console_font_size');
            return saved ? Math.max(9, Math.min(24, parseFloat(saved))) : 12.5;
        } catch (e) {
            return 12.5;
        }
    });

    const [isResizing, setIsResizing] = useState(false);
    const dragStartY = useRef(0);
    const dragStartHeight = useRef(180);

    const handleResizeStart = e => {
        e.preventDefault();
        e.stopPropagation();
        setIsResizing(true);
        setIsExpanded(true);
        dragStartY.current = e.clientY;
        dragStartHeight.current = height;

        document.body.style.cursor = 'ns-resize';
        document.body.style.userSelect = 'none';

        const onPointerMove = moveEvent => {
            const delta = dragStartY.current - moveEvent.clientY;
            const maxHeight = Math.max(250, window.innerHeight * 0.75);
            const newHeight = Math.max(70, Math.min(maxHeight, dragStartHeight.current + delta));
            setHeight(newHeight);
            try {
                localStorage.setItem('pixelblock_console_height', String(Math.round(newHeight)));
            } catch (err) {}
        };

        const onPointerUp = () => {
            setIsResizing(false);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            window.removeEventListener('pointermove', onPointerMove);
            window.removeEventListener('pointerup', onPointerUp);
        };

        window.addEventListener('pointermove', onPointerMove);
        window.addEventListener('pointerup', onPointerUp);
    };

    const handleDecreaseFont = e => {
        e.stopPropagation();
        setFontSize(prev => {
            const next = Math.max(9, Math.round((prev - 1.5) * 10) / 10);
            try {
                localStorage.setItem('pixelblock_console_font_size', String(next));
            } catch (err) {}
            return next;
        });
    };

    const handleIncreaseFont = e => {
        e.stopPropagation();
        setFontSize(prev => {
            const next = Math.min(24, Math.round((prev + 1.5) * 10) / 10);
            try {
                localStorage.setItem('pixelblock_console_font_size', String(next));
            } catch (err) {}
            return next;
        });
    };

    const executeCode = () => {
        const workspace = window.pixelblockWorkspace;
        const isFr = (intl && intl.locale ? intl.locale : (window.pixelblockCurrentLocale || '')).startsWith('fr');

        if (!workspace) {
            setOutput(isFr ? 'Erreur : Aucun espace de travail actif trouvé.\n' : 'Error: No active workspace found.\n');
            setStatus('error');
            setIsExpanded(true);
            return;
        }

        const code = workspaceToPython(workspace);
        setIsExpanded(true);
        setStatus('running');

        // Clear output on run to start fresh
        const startMsg = isFr ? '>>> Exécution du programme Python...\n' : '>>> Running Python program...\n';
        setOutput(startMsg);

        runPython(code, {
            onOutput: text => {
                setOutput(prev => prev + text);
            },
            onError: err => {
                const errMsg = err && err.toString ? err.toString() : String(err);
                setOutput(prev => prev + '\n' + (isFr ? 'Erreur' : 'Error') + ': ' + errMsg + '\n');
                setStatus('error');
            },
            onFinish: err => {
                if (!err) {
                    setOutput(prev => prev + (isFr ? '>>> Programme terminé avec succès.\n' : '>>> Program completed successfully.\n'));
                    setStatus('success');
                } else {
                    setStatus('error');
                }
            }
        });
    };

    useEffect(() => {
        const handleRunEvent = () => {
            executeCode();
        };

        window.addEventListener('pixelblock:runPython', handleRunEvent);

        window.pixelblockConsole = {
            run: executeCode,
            clear: () => {
                setOutput('');
                setStatus('idle');
            },
            open: () => setIsExpanded(true),
            close: () => setIsExpanded(false),
            toggle: () => setIsExpanded(prev => !prev),
            appendOutput: text => setOutput(prev => prev + text)
        };

        return () => {
            window.removeEventListener('pixelblock:runPython', handleRunEvent);
            delete window.pixelblockConsole;
        };
    }, [intl]);

    useEffect(() => {
        if (bodyRef.current && isExpanded) {
            bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
        }
    }, [output, isExpanded]);

    const handleClear = e => {
        e.stopPropagation();
        setOutput('');
        setStatus('idle');
    };

    const toggleExpanded = () => {
        setIsExpanded(prev => !prev);
    };

    return (
        <section className={styles.consoleContainer} aria-label="Console">
            {/* Top border drag resizer */}
            <div
                className={classNames(styles.resizer, {
                    [styles.resizerDragging]: isResizing
                })}
                onPointerDown={handleResizeStart}
                title={intl.formatMessage({
                    id: 'gui.pixelblock.resizeConsole',
                    defaultMessage: 'Drag to resize console'
                })}
            />

            <div
                className={styles.consoleHeader}
                onClick={toggleExpanded}
                role="button"
                tabIndex={0}
                onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        toggleExpanded();
                    }
                }}
            >
                <div className={styles.headerLeft}>
                    <span className={styles.terminalIcon}>&gt;_</span>
                    <span className={styles.consoleTitle}>
                        <FormattedMessage
                            id="gui.pixelblock.console"
                            defaultMessage="Console"
                        />
                    </span>
                    <div className={styles.statusIndicator}>
                        <span
                            className={classNames(styles.statusDot, {
                                [styles.statusDotRunning]: status === 'running',
                                [styles.statusDotSuccess]: status === 'success',
                                [styles.statusDotError]: status === 'error'
                            })}
                        />
                        <span>
                            {status === 'running' ? (
                                <FormattedMessage id="gui.pixelblock.running" defaultMessage="Running..." />
                            ) : status === 'success' ? (
                                <FormattedMessage id="gui.pixelblock.finished" defaultMessage="Finished" />
                            ) : status === 'error' ? (
                                <FormattedMessage id="gui.pixelblock.error" defaultMessage="Error" />
                            ) : (
                                <FormattedMessage id="gui.pixelblock.ready" defaultMessage="Ready" />
                            )}
                        </span>
                    </div>
                </div>

                <div className={styles.headerRight}>
                    <div className={styles.fontControls}>
                        <button
                            type="button"
                            className={styles.fontBtn}
                            onClick={handleDecreaseFont}
                            title={intl.formatMessage({
                                id: 'gui.pixelblock.decreaseFontSize',
                                defaultMessage: 'Decrease text size'
                            })}
                            aria-label="Decrease text size"
                        >
                            A−
                        </button>
                        <button
                            type="button"
                            className={styles.fontBtn}
                            onClick={handleIncreaseFont}
                            title={intl.formatMessage({
                                id: 'gui.pixelblock.increaseFontSize',
                                defaultMessage: 'Increase text size'
                            })}
                            aria-label="Increase text size"
                        >
                            A+
                        </button>
                    </div>

                    {output && (
                        <button
                            type="button"
                            className={styles.clearBtn}
                            onClick={handleClear}
                            title={intl.formatMessage({
                                id: 'gui.pixelblock.clearTitle',
                                defaultMessage: 'Clear console'
                            })}
                        >
                            <FormattedMessage
                                id="gui.pixelblock.clear"
                                defaultMessage="Clear"
                            />
                        </button>
                    )}
                    <span className={styles.toggleIcon}>
                        {isExpanded ? '▼' : '▲'}
                    </span>
                </div>
            </div>

            {isExpanded && (
                <div
                    className={styles.consoleBody}
                    ref={bodyRef}
                    style={{
                        height: `${height}px`,
                        fontSize: `${fontSize}px`
                    }}
                >
                    {output ? (
                        output
                    ) : (
                        <span className={styles.placeholder}>
                            <FormattedMessage
                                id="gui.pixelblock.readyHelp"
                                defaultMessage="Ready. Click &quot;▶ Run Python&quot; to execute your code."
                            />
                        </span>
                    )}
                </div>
            )}
        </section>
    );
};

export default Console;
